import {useEffect} from 'react';

import {evaluateSession, recordActivity, expireSession} from '-/data/session';

// Re-check the session at least this often while the app is open. Keeping it
// short (vs. the timeout) bounds how long after the deadline a logout lands, and
// sidesteps the setTimeout 32-bit overflow on the 30-day "Remember me" window.
const CHECK_INTERVAL_MS = 60 * 1000; // every minute
const ACTIVITY_THROTTLE_MS = 30 * 1000; // persist activity at most every 30s
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'pointerdown', 'scroll'];

const messageFor = reason => reason === 'idle'
    ? 'You were signed out after 1 hour of inactivity. Please sign in again.'
    : 'Your session has expired. Please sign in again.';

// Enforces the session timeout (#1048/#1049): signs the user out after the idle
// or absolute window stored at login passes. Interaction resets the idle clock,
// and the session is re-checked on mount and whenever the app returns to the
// foreground (tab focus / tablet wake) so a long sleep logs out immediately.
export default function useSessionTimeout(currentUser) {
    useEffect(() => {
        if (!currentUser) {
            return undefined;
        }

        let timer;
        let lastRecorded = 0;

        const evaluate = () => {
            const status = evaluateSession();
            if (!status) {
                // Nothing tracked (station login / pre-feature session).
                return;
            }

            if (status.remaining <= 0) {
                expireSession(messageFor(status.reason));
                return;
            }

            clearTimeout(timer);
            timer = setTimeout(evaluate, Math.min(status.remaining, CHECK_INTERVAL_MS));
        };

        const onActivity = () => {
            const now = Date.now();
            if (now - lastRecorded >= ACTIVITY_THROTTLE_MS) {
                lastRecorded = now;
                recordActivity();
            }
        };

        const onResume = () => {
            if (document.visibilityState === 'visible') {
                evaluate();
            }
        };

        evaluate();

        ACTIVITY_EVENTS.forEach(event => window.addEventListener(event, onActivity, {passive: true}));
        document.addEventListener('visibilitychange', onResume);
        window.addEventListener('focus', onResume);

        return () => {
            clearTimeout(timer);
            ACTIVITY_EVENTS.forEach(event => window.removeEventListener(event, onActivity));
            document.removeEventListener('visibilitychange', onResume);
            window.removeEventListener('focus', onResume);
        };
    }, [currentUser]);
}
